import React, { useEffect, useCallback, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'

import useQueryParams from 'hooks/useQueryParams'

import Item from 'ui/modules/lists/checkboxList/item'
import VendorsList from 'ui/pages/vendors'
import { getUserIsAdmin } from 'services/auth/selectors'
import {
  openAddVendorModal,
  openResendInvitationModal,
} from 'services/modal/actions'

import {
  getInvitationsInfo,
  getSelectedIdsOnPage,
  getPaginationInfo,
  getIsInvitationsFetching,
  getInvitationsError,
  getSearchOptions,
  getSearchFilters,
} from 'services/invitations/selectors'
import {
  fetchInvitations,
  selectInvitation,
  selectAll,
  resetSelected,
  fetchSearchOptions,
  clearSearchOptions,
  setSearchFilters,
  fakeRemoveVendors,
} from 'services/invitations/actions'
import * as actionTypesInvitations from 'services/invitations/action-types'
import { showSnackbar } from 'services/snackbar/actions'

const Invitations = () => {
  const dispatch = useDispatch()
  const invitations = useSelector(getInvitationsInfo)
  const selectedIds = useSelector(getSelectedIdsOnPage)
  const paginationInfo = useSelector(getPaginationInfo)
  const isFetching = useSelector(getIsInvitationsFetching)
  const error = useSelector(getInvitationsError)
  const searchOptions = useSelector(getSearchOptions)
  const history = useHistory()
  const query = useQueryParams()
  const { keyword } = useSelector(getSearchFilters)
  const [filters, setFilters] = useState(() => {
    const queryHomologated = query.get('homologated')
    const homologatedFilter =
      queryHomologated == null ? {} : { homologated: !!queryHomologated }

    return { ...homologatedFilter }
  })
  const [searchValue, setSearchValue] = useState('')

  useEffect(() => {
    return () => handleRemoveFilters()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const updateURLParams = useCallback(
    ({ filters }) => {
      const urlParams = []
      if (filters.hasOwnProperty('homologated')) {
        urlParams.push(`homologated=${filters.homologated}`)
      }

      history.push({ search: urlParams.join('&') })
    },
    [history],
  )

  useEffect(() => {
    dispatch(fetchInvitations({ keyword, filters, page: 1 }))
    updateURLParams({ filters })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters])

  const handleInvitationClick = useCallback(
    id => {
      dispatch(selectInvitation(id))
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  )

  const handleGoToPage = useCallback(
    page => {
      dispatch(fetchInvitations({ page, keyword, filters }))
    },
    [dispatch, filters, keyword],
  )

  const selectedCount = useMemo(() => Object.keys(selectedIds).length, [
    selectedIds,
  ])

  const handleBulkSelect = () => {
    if (selectedCount) {
      dispatch(resetSelected())
      return
    }
    dispatch(selectAll())
  }

  const hasFilters = useMemo(() => Object.keys(filters).length || keyword, [
    filters,
    keyword,
  ])

  const handleAddVendor = useCallback(() => {
    dispatch(openAddVendorModal())
  }, [dispatch])

  const handleGetSearchOptions = keyword => {
    dispatch(fetchSearchOptions(keyword, filters))
  }

  const handleClearSearchOptions = useCallback(() => {
    dispatch(clearSearchOptions())
  }, [dispatch])

  const handleRemoveFilters = useCallback(() => {
    dispatch(setSearchFilters({ keyword: '' }))
    setSearchValue('')
    setFilters({})
    handleClearSearchOptions()
  }, [dispatch, handleClearSearchOptions])

  const errorType = useMemo(() => {
    if (isFetching) return { type: '' }
    if (!invitations.length && hasFilters)
      return { type: 'noResultsFilter', action: handleRemoveFilters }
    if (!invitations.length)
      return { type: 'noResults', action: handleAddVendor }
    return {}
  }, [
    isFetching,
    invitations.length,
    hasFilters,
    handleRemoveFilters,
    handleAddVendor,
  ])

  const handleSearch = keyword => {
    dispatch(setSearchFilters({ keyword }))

    dispatch(fetchInvitations({ page: 1 }))
  }

  const handleRemoveInvitations = id => {
    const idsToRemove = id ? [id] : Object.keys(selectedIds)
    const message =
      idsToRemove.length === 1
        ? 'remove:single-invitation'
        : 'remove:multiple-invitations'

    dispatch(fakeRemoveVendors(idsToRemove, filters))
    dispatch(
      showSnackbar({
        message,
        actionToDo: {
          type: actionTypesInvitations.REMOVE,
          payload: { ids: idsToRemove, filters },
        },
        actionOnCancel: {
          type: actionTypesInvitations.FETCH_INVITATIONS,
          payload: { keyword, filters },
        },
      }),
    )
  }

  const handleResendInvitation = useCallback(
    (id, email) => {
      dispatch(openResendInvitationModal(id, email))
    },
    [dispatch],
  )

  const searchFilterOptions = useCallback(options => options, [])
  const userIsAdmin = useSelector(getUserIsAdmin)

  return (
    <VendorsList
      isLoading={isFetching}
      totalVendors={paginationInfo.total}
      totalPages={paginationInfo.pages}
      currentPage={paginationInfo.page}
      goToPage={handleGoToPage}
      handleBulkSelect={handleBulkSelect}
      selectedCount={selectedCount}
      errorType={errorType}
      handleAddVendor={handleAddVendor}
      searchOptions={searchOptions}
      filterOptions={searchFilterOptions}
      getSearchOptions={handleGetSearchOptions}
      clearSearchOptions={handleClearSearchOptions}
      onSearch={handleSearch}
      onRemoveVendors={handleRemoveInvitations}
      totalOnPage={invitations.length}
      filters={null}
      setFilters={null}
      handleRemoveFilters={handleRemoveFilters}
      genericError={error}
      searchValue={searchValue}
      setSearchValue={setSearchValue}
      canAdd={userIsAdmin}
      canDelete={userIsAdmin}
    >
      {invitations.map(invitation => (
        <Item
          key={invitation.id}
          title={invitation.name}
          id={invitation.id}
          email={invitation.email ?? ''}
          vendorId={invitation.vendorId}
          isHomologated={invitation.homologated}
          showHomologated={false}
          showRequirements={false}
          onSelect={handleInvitationClick}
          isSelected={!!selectedIds[invitation.id]}
          isPending={!invitation.vendorId}
          onRemoveVendor={handleRemoveInvitations}
          canDelete={userIsAdmin}
          showResend={userIsAdmin}
          onResendInvitation={handleResendInvitation}
        />
      ))}
    </VendorsList>
  )
}

export default Invitations
